import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccountingService } from '../../../services/accounting.service';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-accounting-transactions',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TagModule,
    InputTextModule,
    DialogModule,
    FormsModule,
    SelectModule,
    DatePickerModule,
    TooltipModule
  ],
  template: `
    <div class="p-4">
      <div class="flex justify-content-between align-items-center mb-4">
        <h1 class="text-3xl font-bold m-0 text-primary">Journal Ledger</h1>
        <div class="flex gap-3 align-items-center">
          <div class="flex align-items-center gap-2 mr-3">
             <label for="showBalances" class="font-medium text-secondary">Show Balances</label>
             <p-button 
               [icon]="showBalances ? 'pi pi-eye' : 'pi pi-eye-slash'" 
               [severity]="showBalances ? 'info' : 'secondary'" 
               [text]="true" 
               (onClick)="showBalances = !showBalances"
               pTooltip="Toggle balance visibility"
             ></p-button>
          </div>
          <p-button label="Record Entry" icon="pi pi-plus" (onClick)="showDialog()" class="p-button-raised p-button-primary"></p-button>
        </div>
      </div>

      <p-table #dt [value]="transactions" [rows]="10" [paginator]="true" [globalFilterFields]="['description', 'reference_number']"
               responsiveLayout="scroll" styleClass="p-datatable-gridlines p-datatable-striped shadow-2">
        <ng-template pTemplate="caption">
          <div class="flex justify-content-between align-items-center">
            <span class="text-xl font-medium">Recent Transactions</span>
            <span class="p-input-icon-left">
              <i class="pi pi-search"></i>
              <input pInputText type="text" (input)="dt.filterGlobal($any($event.target).value, 'contains')" placeholder="Search ledger..." class="w-20rem" />
            </span>
          </div>
        </ng-template>
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 15%">Date</th>
            <th>Description</th>
            <th style="width: 18%">Source (Credit)</th>
            <th style="width: 18%">Destination (Debit)</th>
            <th style="width: 10%">Reference</th>
            <th style="width: 10%" class="text-right">Amount</th>
            <th style="width: 5%"></th>
          </tr>
          <tr class="quick-entry-row">
            <td>
              <p-datepicker [(ngModel)]="quickTx.date" [showIcon]="false" appendTo="body" placeholder="Date" styleClass="w-full" [fluid]="true"></p-datepicker>
            </td>
            <td>
              <input pInputText [(ngModel)]="quickTx.description" placeholder="Quick description..." class="w-full" />
            </td>
            <td>
              <p-select [options]="accounts" optionLabel="name" optionValue="id" [(ngModel)]="quickTx.from_account_id" 
                       placeholder="Source" [filter]="true" appendTo="body" styleClass="w-full"></p-select>
            </td>
            <td>
              <p-select [options]="accounts" optionLabel="name" optionValue="id" [(ngModel)]="quickTx.to_account_id" 
                       placeholder="Dest" [filter]="true" appendTo="body" styleClass="w-full"></p-select>
            </td>
            <td>
              <input pInputText [(ngModel)]="quickTx.reference_number" placeholder="Ref #" class="w-full" />
            </td>
            <td>
              <input pInputText type="number" [(ngModel)]="quickTx.amount" placeholder="0.00" class="w-full text-right" />
            </td>
            <td class="text-center">
              <p-button icon="pi pi-plus" (onClick)="saveQuickEntry()" [disabled]="!isQuickEntryValid()" 
                        [rounded]="true" severity="success" [text]="true" tooltipPosition="left" pTooltip="Quick Add"></p-button>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-tx>
          <tr>
            <td>{{ tx.date | date:'mediumDate' }}</td>
            <td>
              <div class="font-bold">{{ tx.description }}</div>
              <div class="text-xs text-500" *ngIf="tx.contact_id">{{ tx.contact_id }}</div>
            </td>
            <td>
              <div class="flex flex-column gap-1">
                <p-tag [value]="getAccountName(tx.from_account_id)" severity="secondary" [style]="{background: '#f1f5f9', color: '#475569'}"></p-tag>
                <div *ngIf="showBalances" class="text-xs text-500 px-1">
                    <span class="opacity-70">{{ tx.from_balance_before | currency }}</span>
                    <i class="pi pi-arrow-right mx-1" style="font-size: 0.6rem"></i>
                    <span class="font-medium">{{ tx.from_balance_after | currency }}</span>
                </div>
              </div>
            </td>
            <td>
              <div class="flex flex-column gap-1">
                <p-tag [value]="getAccountName(tx.to_account_id)" severity="info" [style]="{background: '#e0f2fe', color: '#0284c7'}"></p-tag>
                <div *ngIf="showBalances" class="text-xs text-500 px-1">
                    <span class="opacity-70">{{ tx.to_balance_before | currency }}</span>
                    <i class="pi pi-arrow-right mx-1" style="font-size: 0.6rem"></i>
                    <span class="font-medium">{{ tx.to_balance_after | currency }}</span>
                </div>
              </div>
            </td>
            <td><code class="text-xs bg-gray-100 p-1 border-round">{{ tx.reference_number }}</code></td>
            <td class="text-right font-bold text-lg">
              {{ tx.amount | currency }}
            </td>
            <td></td>
          </tr>
        </ng-template>
      </p-table>

      <p-dialog [(visible)]="displayDialog" [modal]="true" header="New Journal Entry" [style]="{width: '500px'}" styleClass="p-fluid">
        <div class="p-3">
          <div class="field mb-3">
            <label class="block mb-2 font-bold">Description</label>
            <input pInputText [(ngModel)]="newTx.description" placeholder="e.g. Monthly Rent" />
          </div>
          
          <div class="flex gap-3 mb-3">
            <div class="flex-1">
              <label class="block mb-2 font-bold text-red-600">Source (Credit)</label>
              <p-select [options]="accounts" optionLabel="name" optionValue="id" [(ngModel)]="newTx.from_account_id" 
                       placeholder="Select source" appendTo="body" [filter]="true"></p-select>
            </div>
            
            <div class="flex-1">
              <label class="block mb-2 font-bold text-green-600">Destination (Debit)</label>
              <p-select [options]="accounts" optionLabel="name" optionValue="id" [(ngModel)]="newTx.to_account_id" 
                       placeholder="Select destination" appendTo="body" [filter]="true"></p-select>
            </div>
          </div>

          <div class="field mb-3">
            <label class="block mb-2 font-bold">Contact (Vendor/Customer)</label>
            <p-select [options]="contacts" optionLabel="name" optionValue="id" [(ngModel)]="newTx.contact_id" 
                     placeholder="Optional" appendTo="body" [filter]="true" [showClear]="true"></p-select>
          </div>

          <div class="flex gap-3 mb-3">
            <div class="flex-1">
              <label class="block mb-2 font-bold">Date</label>
              <p-datepicker [(ngModel)]="newTx.date" [showIcon]="true" appendTo="body"></p-datepicker>
            </div>
            
            <div class="flex-1">
              <label class="block mb-2 font-bold">Amount</label>
              <input pInputText type="number" [(ngModel)]="newTx.amount" placeholder="0.00" />
            </div>
          </div>

          <div class="field">
            <label class="block mb-2 font-bold">Reference #</label>
            <input pInputText [(ngModel)]="newTx.reference_number" placeholder="Optional" />
          </div>
        </div>
        <ng-template pTemplate="footer">
          <p-button label="Cancel" icon="pi pi-times" [text]="true" (onClick)="displayDialog = false"></p-button>
          <p-button label="Record Entry" icon="pi pi-check" (onClick)="saveTransaction()" [disabled]="!newTx.from_account_id || !newTx.to_account_id || !newTx.amount"></p-button>
        </ng-template>
      </p-dialog>
    </div>
  `
})
export class TransactionsComponent implements OnInit {
  transactions: any[] = [];
  accounts: any[] = [];
  contacts: any[] = [];
  displayDialog = false;
  showBalances = false;

  newTx: any = {
    amount: 0,
    date: new Date(),
    description: '',
    from_account_id: '',
    to_account_id: '',
    contact_id: '',
    reference_number: ''
  };

  quickTx: any = {
    amount: null,
    date: new Date(),
    description: '',
    from_account_id: '',
    to_account_id: '',
    reference_number: ''
  };

  constructor(private accountingService: AccountingService) { }

  ngOnInit() {
    this.loadData();
  }

  private loadData() {
    this.accountingService.getTransactions().subscribe(data => this.transactions = data);
    this.accountingService.getAccounts().subscribe(data => this.accounts = data);
    this.accountingService.getContacts().subscribe(data => this.contacts = data);
  }

  getAccountName(id: string): string {
    const acc = this.accounts.find(a => a.id === id);
    return acc ? acc.name : (id || 'Unknown');
  }

  showDialog() {
    this.newTx = {
      amount: 0,
      date: new Date(),
      description: '',
      from_account_id: '',
      to_account_id: '',
      contact_id: '',
      reference_number: ''
    };
    this.displayDialog = true;
  }

  saveTransaction() {
    const formattedTx = {
      ...this.newTx,
      date: this.newTx.date.toISOString().split('T')[0]
    };

    this.accountingService.createTransaction(formattedTx).subscribe(() => {
      this.displayDialog = false;
      this.loadData();
    });
  }

  isQuickEntryValid(): boolean {
    return !!(this.quickTx.description &&
      this.quickTx.from_account_id &&
      this.quickTx.to_account_id &&
      this.quickTx.amount &&
      this.quickTx.date);
  }

  saveQuickEntry() {
    const formattedTx = {
      ...this.quickTx,
      date: this.quickTx.date instanceof Date ? this.quickTx.date.toISOString().split('T')[0] : this.quickTx.date
    };

    this.accountingService.createTransaction(formattedTx).subscribe(() => {
      this.loadData();
      this.resetQuickEntry();
    });
  }

  private resetQuickEntry() {
    this.quickTx = {
      amount: null,
      date: new Date(),
      description: '',
      from_account_id: '',
      to_account_id: '',
      reference_number: ''
    };
  }
}
